/**
 * Core HTTP client implementation
 */

import { NetworkError } from './errors.js';
import type {
  ClientConfig,
  NetworkClient,
  NetworkResponse,
  RequestOptions,
  Result,
  RetryConfig,
} from './types.js';

const DEFAULT_TIMEOUT = 30_000;
const DEFAULT_RETRY_ATTEMPTS = 5;
const DEFAULT_RETRY_DELAY = 2000;
const DEFAULT_MAX_DELAY = 30_000;
const DEFAULT_RETRYABLE_STATUSES = [408, 429, 500, 502, 503, 504];

/**
 * Build URL with query parameters
 *
 * Properly handles base URLs with path components (e.g., https://api.example.com/v1)
 * by joining paths rather than replacing them.
 */
function buildUrl(baseUrl: string, path: string, params?: Record<string, string | number | boolean | undefined>): string {
  // Handle absolute URLs
  const isAbsolute = path.startsWith('http://') || path.startsWith('https://');

  let url: URL;
  if (isAbsolute) {
    url = new URL(path);
  } else {
    // Parse the base URL to preserve its path component
    const base = new URL(baseUrl || 'http://localhost');
    // Split path from inline query string so the ? doesn't get encoded
    const qIdx = path.indexOf('?');
    const pathPart = qIdx >= 0 ? path.slice(0, qIdx) : path;
    const queryPart = qIdx >= 0 ? path.slice(qIdx + 1) : undefined;
    const basePath = base.pathname.endsWith('/') ? base.pathname.slice(0, -1) : base.pathname;
    const newPath = pathPart.startsWith('/') ? pathPart : '/' + pathPart;
    base.pathname = basePath + newPath;
    // Preserve any inline query parameters
    if (queryPart) {
      const inlineParams = new URLSearchParams(queryPart);
      for (const [key, value] of inlineParams) {
        base.searchParams.set(key, value);
      }
    }
    url = base;
  }

  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined) {
        url.searchParams.set(key, String(value));
      }
    }
  }

  return isAbsolute ? url.toString() : url.pathname + url.search;
}

/**
 * Serialize request body
 */
function serializeBody(body: unknown, rawBody?: boolean): BodyInit | undefined {
  if (body === undefined || body === null) {
    return undefined;
  }

  if (rawBody) {
    return body as BodyInit;
  }

  // Pass through native body types
  if (
    body instanceof FormData ||
    body instanceof URLSearchParams ||
    body instanceof Blob ||
    body instanceof ArrayBuffer ||
    typeof body === 'string'
  ) {
    return body;
  }

  // JSON serialize objects
  return JSON.stringify(body);
}

/**
 * Get content type for body
 */
function getContentType(body: unknown, rawBody?: boolean): string | undefined {
  if (body === undefined || body === null || rawBody) {
    return undefined;
  }

  if (body instanceof FormData) {
    return undefined; // Let browser set with boundary
  }

  if (body instanceof URLSearchParams) {
    return 'application/x-www-form-urlencoded';
  }

  if (typeof body === 'string') {
    return 'text/plain';
  }

  return 'application/json';
}

/**
 * Parse response based on content type or explicit type
 */
async function parseResponse<T>(
  response: Response,
  responseType?: 'json' | 'text' | 'blob' | 'arrayBuffer' | 'formData' | 'stream'
): Promise<T> {
  // Empty response
  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return undefined as T;
  }

  // Explicit response type
  if (responseType) {
    switch (responseType) {
      case 'json':
        return response.json() as Promise<T>;
      case 'text':
        return response.text() as Promise<T>;
      case 'blob':
        return response.blob() as Promise<T>;
      case 'arrayBuffer':
        return response.arrayBuffer() as Promise<T>;
      case 'formData':
        return response.formData() as Promise<T>;
      case 'stream':
        // Return the raw body without consuming it
        return response.body as T;
    }
  }

  // Infer from content type
  const contentType = response.headers.get('content-type') ?? '';

  if (contentType.includes('application/json')) {
    return response.json() as Promise<T>;
  }

  if (contentType.includes('text/')) {
    return response.text() as Promise<T>;
  }

  // Default to JSON for unknown types
  try {
    return await response.json() as T;
  } catch {
    return response.text() as Promise<T>;
  }
}

/**
 * Calculate retry delay with exponential backoff
 */
function calculateRetryDelay(
  attempt: number,
  config: RetryConfig,
  response?: Response
): number {
  // Check for Retry-After header
  const retryAfter = response?.headers.get('retry-after');
  if (retryAfter) {
    const seconds = parseInt(retryAfter, 10);
    if (!isNaN(seconds)) {
      return Math.min(seconds * 1000, config.maxDelay ?? DEFAULT_MAX_DELAY);
    }
  }

  const baseDelay = config.delay;
  const maxDelay = config.maxDelay ?? DEFAULT_MAX_DELAY;

  if (config.backoff !== false) {
    // Exponential backoff with jitter
    const exponentialDelay = baseDelay * Math.pow(2, attempt);
    const jitter = Math.random() * 0.3 * exponentialDelay;
    return Math.min(exponentialDelay + jitter, maxDelay);
  }

  return Math.min(baseDelay, maxDelay);
}

/**
 * Sleep for a given number of milliseconds
 */
function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Create a timeout signal that aborts after the specified time
 */
function createTimeoutSignal(timeout: number): { signal: AbortSignal; clear: () => void } {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  return {
    signal: controller.signal,
    clear: () => clearTimeout(timeoutId),
  };
}

/**
 * Combine multiple abort signals.
 *
 * Returns the combined signal alongside a `clear()` that detaches the listeners
 * it attached — the caller MUST invoke it once the attempt settles.
 *
 * Cleanup is not optional here. This is called once per retry attempt, and one
 * of the inputs is the *caller's* signal, which outlives the request. Without
 * detaching, a long-lived signal accumulated one listener per attempt per
 * request (up to 6 by default) and never shed them. `{ once: true }` does not
 * cover this: it only fires on abort, so on the success path — the common one —
 * nothing was ever removed. tdd-0075.
 */
function combineSignals(
  signals: (AbortSignal | undefined)[],
): { signal: AbortSignal | undefined; clear: () => void } {
  const validSignals = signals.filter((s): s is AbortSignal => s !== undefined);

  if (validSignals.length === 0) return { signal: undefined, clear: () => {} };
  if (validSignals.length === 1) return { signal: validSignals[0], clear: () => {} };

  const controller = new AbortController();

  for (const signal of validSignals) {
    if (signal.aborted) {
      controller.abort();
      return { signal: controller.signal, clear: () => {} };
    }
  }

  // Tracked so each listener can be detached from the signal it was added to.
  const attached: { signal: AbortSignal; handler: () => void }[] = [];
  for (const signal of validSignals) {
    const handler = () => controller.abort();
    signal.addEventListener('abort', handler, { once: true });
    attached.push({ signal, handler });
  }

  return {
    signal: controller.signal,
    clear: () => {
      for (const { signal, handler } of attached) {
        signal.removeEventListener('abort', handler);
      }
      attached.length = 0;
    },
  };
}

/**
 * Create an HTTP client instance
 */
export function createClient(config: ClientConfig = {}): NetworkClient {
  const {
    baseUrl = '',
    timeout: defaultTimeout = DEFAULT_TIMEOUT,
    headers: defaultHeaders = {},
    retry: defaultRetry,
    credentials: defaultCredentials,
    onRequest,
    onResponse,
    onError,
    fetch: customFetch = globalThis.fetch,
    auth,
  } = config;

  // Token refresh state (scoped per client instance)
  let isRefreshing = false;
  let refreshPromise: Promise<boolean> | null = null;
  const pendingRequests: Array<{
    resolve: (value: unknown) => void;
    reject: (error: unknown) => void;
    retry: () => Promise<unknown>;
  }> = [];

  async function request<T = unknown, TBody = unknown>(
    url: string,
    options: RequestOptions<TBody> = {}
  ): Promise<NetworkResponse<T>> {
    const {
      method = 'GET',
      headers = {},
      body,
      params,
      timeout = defaultTimeout,
      retry,
      signal: userSignal,
      responseType,
      credentials = defaultCredentials,
      cache,
      rawBody,
    } = options;

    // Build full URL
    const fullUrl = buildUrl(baseUrl, url, params);
    const absoluteUrl = baseUrl && !url.startsWith('http') ? new URL(fullUrl, baseUrl).toString() : fullUrl;

    // Merge retry config
    const retryConfig: RetryConfig = {
      attempts: retry?.attempts ?? defaultRetry?.attempts ?? DEFAULT_RETRY_ATTEMPTS,
      delay: retry?.delay ?? defaultRetry?.delay ?? DEFAULT_RETRY_DELAY,
      backoff: retry?.backoff ?? defaultRetry?.backoff ?? true,
      maxDelay: retry?.maxDelay ?? defaultRetry?.maxDelay ?? DEFAULT_MAX_DELAY,
      retryableStatuses: retry?.retryableStatuses ?? defaultRetry?.retryableStatuses ?? DEFAULT_RETRYABLE_STATUSES,
    };

    // Build headers
    const contentType = getContentType(body, rawBody);
    const mergedHeaders: Record<string, string> = {
      ...defaultHeaders,
      ...headers,
    };
    if (contentType && !mergedHeaders['content-type'] && !mergedHeaders['Content-Type']) {
      mergedHeaders['content-type'] = contentType;
    }

    // Build request init
    let init: RequestInit = {
      method,
      headers: mergedHeaders,
      body: serializeBody(body, rawBody),
      credentials,
      cache,
    };

    // Apply request interceptor
    if (onRequest) {
      init = await onRequest(absoluteUrl, init);
    }

    // Execute with retries
    let lastError: NetworkError | undefined;
    let attempt = 0;

    while (attempt <= retryConfig.attempts) {
      // Create timeout signal
      const timeoutHandle = createTimeoutSignal(timeout);

      // Combine signals. `clear()` detaches the abort listeners this attaches to
      // the caller's signal — released in the `finally` below, so every exit
      // path (success, HTTP error, retry, throw) sheds them. See combineSignals.
      const combinedSignals = combineSignals([userSignal, timeoutHandle.signal]);
      const requestInit: RequestInit = { ...init, signal: combinedSignals.signal };

      try {
        const response = await customFetch(absoluteUrl, requestInit);
        const isStream = responseType === 'stream';

        // For streams, clear timeout after headers arrive (streaming continues long after)
        // For non-streams, clear after full response is received (handled below)
        if (isStream) {
          timeoutHandle.clear();
        }

        // Check for HTTP errors first for streams (don't consume body via parseResponse)
        if (!response.ok) {
          if (!isStream) timeoutHandle.clear();

          // For streams, read error body as text; for others, parse normally
          let errorData: unknown;
          try {
            errorData = isStream
              ? await response.text()
              : await parseResponse(response, responseType);
          } catch {
            // Ignore parse errors for error responses
          }

          const error = NetworkError.http(absoluteUrl, method, response, errorData);

          // Check if we should retry
          const shouldRetry = attempt < retryConfig.attempts &&
            retryConfig.retryableStatuses?.includes(response.status);

          if (shouldRetry) {
            lastError = error;
            const delay = calculateRetryDelay(attempt, retryConfig, response);
            await sleep(delay);
            attempt++;
            continue;
          }

          throw error;
        }

        // Parse response body
        let data: T;
        if (isStream) {
          // Return raw body without consuming it
          data = response.body as T;
        } else {
          timeoutHandle.clear();
          try {
            data = await parseResponse<T>(response, responseType);
          } catch (parseError) {
            throw NetworkError.parse(absoluteUrl, method, parseError instanceof Error ? parseError : undefined);
          }
        }

        // Apply response interceptor (skip for streams — interceptor expects parsed data)
        const finalData = (!isStream && onResponse) ? await onResponse(response, data) : data;

        return {
          data: finalData,
          status: response.status,
          statusText: response.statusText,
          headers: response.headers,
          response,
        };
      } catch (error) {
        timeoutHandle.clear();

        // Convert to NetworkError
        let networkError: NetworkError;
        if (error instanceof NetworkError) {
          networkError = error;
        } else if (error instanceof Error && error.name === 'AbortError') {
          // Check if it was a timeout or user abort
          if (userSignal?.aborted) {
            networkError = NetworkError.abort(absoluteUrl, method);
          } else {
            networkError = NetworkError.timeout(absoluteUrl, method, timeout);
          }
        } else {
          networkError = NetworkError.from(error, absoluteUrl, method);
        }

        // Check if we should retry
        const shouldRetry = attempt < retryConfig.attempts && networkError.retryable;

        if (shouldRetry) {
          lastError = networkError;
          const delay = calculateRetryDelay(attempt, retryConfig);
          await sleep(delay);
          attempt++;
          continue;
        }

        // Call error handler
        if (onError) {
          await onError(networkError);
        }

        throw networkError;
      } finally {
        // Detach this attempt's abort listeners from the caller's signal.
        // Placed here rather than beside each `timeoutHandle.clear()` because
        // the loop body has five exit paths (stream, HTTP error, retry-continue,
        // success, throw) and mirroring them by hand is how a leak like this
        // gets reintroduced. tdd-0075.
        combinedSignals.clear();
      }
    }

    // Should not reach here, but just in case
    throw lastError ?? new NetworkError('Request failed', { code: 'UNKNOWN' });
  }

  /**
   * Wraps request with 401/403 → refresh → retry logic.
   * If auth config is not set, passes through directly.
   *
   * 401: token expired — refresh and retry (existing behavior)
   * 403: insufficient scopes — refresh to pick up newly-granted
   *       scopes/tier, then retry once. If still 403 after refresh,
   *       the user genuinely lacks permission → throw.
   */
  async function requestWithAuth<T = unknown, TBody = unknown>(
    url: string,
    options: RequestOptions<TBody> = {}
  ): Promise<NetworkResponse<T>> {
    try {
      return await request<T, TBody>(url, options);
    } catch (error) {
      if (
        !auth ||
        !(error instanceof NetworkError) ||
        (error.status !== 401 && error.status !== 403)
      ) {
        throw error;
      }

      const originalStatus = error.status;

      // If already refreshing, queue this request
      if (isRefreshing) {
        return new Promise<NetworkResponse<T>>((resolve, reject) => {
          pendingRequests.push({
            resolve: resolve as (value: unknown) => void,
            reject,
            retry: () => request<T, TBody>(url, options),
          });
        });
      }

      // Start refresh
      isRefreshing = true;
      refreshPromise = auth.onUnauthorized();

      try {
        const success = await refreshPromise;

        if (success) {
          // Process queued requests — CONCURRENTLY.
          //
          // `await retry()` inside the loop made every queued request wait for
          // all the ones before it, so N requests that piled up behind a single
          // refresh paid N round trips in series instead of one in parallel.
          // The whole point of the queue is that these are independent.
          //
          // `auth.ts` (installAuthRefresh) already drains its queue this exact
          // way — the two implementations of one behaviour had diverged, and
          // this was the wrong one. tdd-0075.
          const queued = [...pendingRequests];
          pendingRequests.length = 0;
          for (const { resolve, reject, retry } of queued) {
            retry().then(resolve, reject);
          }

          // Retry the original request
          return await request<T, TBody>(url, options);
        } else {
          // Refresh failed — reject all queued requests
          const queued = [...pendingRequests];
          pendingRequests.length = 0;
          for (const { reject } of queued) {
            reject(error);
          }
          throw error;
        }
      } catch (retryError) {
        // If we got 403 originally and still 403 after refresh,
        // the user genuinely lacks permission — don't loop, just throw
        if (
          originalStatus === 403 &&
          retryError instanceof NetworkError &&
          retryError.status === 403
        ) {
          throw retryError;
        }
        throw retryError;
      } finally {
        isRefreshing = false;
        refreshPromise = null;
      }
    }
  }

  async function safeRequest<T = unknown, TBody = unknown>(
    url: string,
    options?: RequestOptions<TBody>
  ): Promise<Result<T>> {
    try {
      const response = await requestWithAuth<T, TBody>(url, options);
      return { ok: true, data: response.data, response };
    } catch (error) {
      return { ok: false, error: NetworkError.from(error) };
    }
  }

  return {
    get: <T = unknown>(url: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
      requestWithAuth<T>(url, { ...options, method: 'GET' }),

    post: <T = unknown, TBody = unknown>(url: string, body?: TBody, options?: Omit<RequestOptions<TBody>, 'method' | 'body'>) =>
      requestWithAuth<T, TBody>(url, { ...options, method: 'POST', body }),

    put: <T = unknown, TBody = unknown>(url: string, body?: TBody, options?: Omit<RequestOptions<TBody>, 'method' | 'body'>) =>
      requestWithAuth<T, TBody>(url, { ...options, method: 'PUT', body }),

    patch: <T = unknown, TBody = unknown>(url: string, body?: TBody, options?: Omit<RequestOptions<TBody>, 'method' | 'body'>) =>
      requestWithAuth<T, TBody>(url, { ...options, method: 'PATCH', body }),

    delete: <T = unknown>(url: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
      requestWithAuth<T>(url, { ...options, method: 'DELETE' }),

    request: requestWithAuth,
    safeRequest,
  };
}
